/**
 * Fusion Candidates layer (#370 de-flood) — the POST-fusion BELOW-URGENT mass.
 *
 * When the F.1 density-arm gate (response-level.ts clearsDensityFloor) demotes a
 * fused hotspot cluster that didn't clear the corroboration floor, it lands at
 * IMS Response Level 1/2 and drops off the LOCKED urgent surface. Those rows are
 * not noise — they are real fused candidates below the "Major" bar — so they get
 * their own opt-in home here instead of flooding Urgent Alerts.
 *
 * This is the sibling of HighRiskHotspotsLayer, partitioned by band: the urgent
 * layer fetches min_response_level=3 (the gated reds); THIS layer fetches
 * max_response_level=CANDIDATE_MAX_RESPONSE_LEVEL (2). Together they cover every
 * confidence≥60 row exactly once (pinned by hotspotBands.test.ts).
 *
 * Render: the existing muted L1/L2 anomaly treatment (styles/responseLevel —
 * reserved URGENT-RED at low alpha, SIZE encodes the level, NEVER pulsing). The
 * whole layer is default OFF and self-gates to regional+ zoom (one shared
 * DistanceDisplayCondition), so the national Default view stays clean and the
 * candidates only appear when an operator opts in and descends. A picked
 * candidate routes to the SAME HighRiskHotspotBody side panel as an urgent red
 * (it is the same feature shape), so no new detail surface is needed.
 *
 * Imperative installer (MapView owns the viewer): requestRefresh on camera moves,
 * setVisible on the toggle, pick from the central click handler.
 */
import * as Cesium from 'cesium';
import type {
  HighRiskHotspotFeature,
  HighRiskHotspotFeatureCollection,
} from '@tsr/shared-types';
import { fetchHighRiskHotspots, type BboxQuery, type HighRiskHotspotFetchOpts } from '../api';
import {
  hotspotLevelStyle,
  CANDIDATE_MAX_RESPONSE_LEVEL,
  isResponseLevel,
  RESPONSE_LEVEL_COLORS,
} from '../styles/responseLevel';
import { anomalyLevelBillboardColor } from './anomalyMarker';
import { renderDiscGlyph } from '../discGlyphComposite';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { isWithinCanadaScope } from '../canadaScope';

const REFRESH_DEBOUNCE_MS = 300;
const CANDIDATE_PREFIX = 'fusioncandidate:';

export interface FusionCandidatesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: HighRiskHotspotFetchOpts,
  ) => Promise<HighRiskHotspotFeatureCollection>;
}

export interface FusionCandidatesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its candidate feature (null if not ours). */
  pick(entity: unknown): HighRiskHotspotFeature | null;
  destroy(): void;
}

export function installFusionCandidatesLayer(
  viewer: Cesium.Viewer,
  opts: FusionCandidatesLayerOptions = {},
): FusionCandidatesLayerHandle {
  const fetcher = opts.fetcher ?? fetchHighRiskHotspots;
  const ds = new Cesium.CustomDataSource('fusion-candidates');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the de-flood gate). The candidates are
  // below-urgent, so they never compete with the urgent reds at the national view.
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const entityToFeature = new Map<Cesium.Entity, HighRiskHotspotFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load resolving after a newer one.
  let loadGen = 0;

  const render = (fc: HighRiskHotspotFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      // Muted L1/L2 treatment (never pulses). An out-of-range level resolves to
      // the muted 'unknown' style — never dropped (E.1.7 lesson).
      const level = feature.properties.response_level;
      const style = hotspotLevelStyle(level);
      const [lon, lat] = feature.geometry.coordinates;
      // Canada-scope render guard (round-2 completes the round-1 finding-1 family across every
      // fan-participating hotspot family). This layer is default-OFF + regional-gated so it can't
      // reach the national default view, but an ocean-coordinate shared-DB fixture would still
      // render at regional zoom if the operator enables it — skip anything outside Canada.
      if (!isWithinCanadaScope(lon, lat)) continue;
      // Icon one-swing (#564, §C.1): the EXACT-ACT DISC — ONE billboard: a
      // RESPONSE-LEVEL-filled disc (muted L1 slate / L2 blue) with the white `hot-spot`
      // glyph knocked out. This is the SAME hotspot family as HighRiskHotspotsLayer
      // (the demoted below-urgent mass), so it carries the `hot-spot` glyph too — FIXES
      // the prior glyph-less muted dot. STATIC (L1/L2 never pulse), muted alpha.
      const fillHex = isResponseLevel(level) ? RESPONSE_LEVEL_COLORS[level] : RESPONSE_LEVEL_COLORS[1];
      const image = renderDiscGlyph('hot-spot', { fill: fillHex }) ?? undefined;
      const dot = ds.entities.add({
        id: `${CANDIDATE_PREFIX}${feature.id}`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        billboard: {
          image,
          width: style.baseSize,
          height: style.baseSize,
          color: anomalyLevelBillboardColor(false, style.alpha),
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          // Regional-zoom render gate (hidden at country).
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(dot, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      // The demoted L1/L2 band — the exact complement of the urgent layer's
      // min_response_level=3 fetch (partition: never both, never dropped).
      const fc = await fetcher(bbox, ctl.signal, {
        minConfidence: 60,
        maxResponseLevel: CANDIDATE_MAX_RESPONSE_LEVEL,
      });
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchFusionCandidates failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): HighRiskHotspotFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
